import React, { useState } from 'react'
import { useSelector } from 'hooks/redux'
import PropTypes from 'prop-types'
import { Header } from 'components/patterns'
import { getIsSimulating } from 'selectors/navigator'
import {
  NEW,
  TEST,
  RETURNED,
  VERIFIED,
  ACTIVE,
  OBSOLETE,
  CHECPLUS_MODULE_NAMES,
} from 'utils/constants'
import strings from 'utils/strings.js'
import { classNames } from 'utils/utils'
import { useModalVisibility } from 'hooks/useModalVisibility'

import {
  ModalBody,
  ModalButton,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalMainContainer,
  ModalSmall,
} from 'components/patterns/Modal'
import IconAlert from 'components/patterns/Icon/components/Icon__Alert'
import DesignButton from '../LifeCycleButtons/DesignButton'
import TestButton from '../LifeCycleButtons/TestButton'
import ActiveButton from '../LifeCycleButtons/ActiveButton'

import withStyle, { PhaseConnector } from './LifeCycleEditor.style'
import workFlow from './workFlow'

const LifeCycleEditor = (props) => {
  const {
    currentStatus,
    setStatus,
    className,
    moduleName,
    revision,
    setConfirmActivate,
  } = props
  const isSimulating = useSelector(getIsSimulating)
  const [showModal, , setShowModal] = useModalVisibility()
  const [modalProps, setModalProps] = useState({})

  const handleClose = () => {
    setShowModal(false)
    setModalProps({})
  }

  const handleClick = (actionType) => () => {
    const { [actionType]: action } = workFlow(
      moduleName,
      currentStatus,
      revision
    )
    const { prompt = null, changeStatusTo } = action
    const execute = () => {
      setStatus(changeStatusTo, currentStatus)
      handleClose()
    }

    if (prompt !== null) {
      setShowModal(true)
      setModalProps({
        ...prompt,
        onConfirm: execute,
      })
    } else if (changeStatusTo === ACTIVE) {
      setConfirmActivate(true)
    } else {
      execute()
    }
  }

  const isDesign = currentStatus === NEW || currentStatus === RETURNED
  const isTest = currentStatus === TEST
  const isActive = currentStatus === ACTIVE

  return (
    <div className={classNames(className, 'life-cycle-editor')}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="188"
        height="52"
        viewBox="0 0 188 52"
      >
        <g fill="none" fillRule="evenodd">
          <PhaseConnector
            isDisabled={isActive || isSimulating}
            strokeLinecap="square"
            d="M31.5 17H107M162 17h-55"
          />

          <DesignButton
            isSelected={isDesign}
            isSelectable={isTest && !isSimulating}
            isDisabled={isActive}
            onClick={handleClick}
          />
          <TestButton
            isSelected={isTest}
            isSelectable={isDesign && !isSimulating}
            isDisabled={isActive}
            onClick={handleClick}
          />
          <ActiveButton
            isSelected={isActive}
            isSelectable={isTest && !isSimulating}
            isDisabled={isDesign}
            onClick={handleClick}
          />
        </g>
      </svg>
      {showModal && (
        <ModalSmall data-testid="lifecycle-status-change-confirm-modal">
          <ModalHeader>
            <Header type="h3">
              <IconAlert /> {modalProps.header}
            </Header>
            <ModalCloseButton onClick={handleClose} />
          </ModalHeader>
          <ModalBody>
            <ModalMainContainer>
              <ModalContent>{modalProps.message}</ModalContent>
            </ModalMainContainer>
          </ModalBody>
          <ModalFooter>
            <ModalButton type="secondary" onClick={handleClose}>
              {strings.cancel}
            </ModalButton>
            <ModalButton type="primary" onClick={modalProps.onConfirm}>
              {modalProps.yesButtonText}
            </ModalButton>
          </ModalFooter>
        </ModalSmall>
      )}
    </div>
  )
}

LifeCycleEditor.propTypes = {
  className: PropTypes.string,
  moduleName: PropTypes.oneOf(Object.values(CHECPLUS_MODULE_NAMES)),
  currentStatus: PropTypes.oneOf([
    NEW,
    TEST,
    RETURNED,
    VERIFIED,
    ACTIVE,
    OBSOLETE,
  ]),
  setStatus: PropTypes.func,
  partNumber: PropTypes.string,
  onSendToTest: PropTypes.func,
  featureIsValid: PropTypes.bool,
  setFailedSendToTest: PropTypes.func,
  setConfirmActivate: PropTypes.func,
  revision: PropTypes.string,
  yesButtonLoading: PropTypes.bool,
  closeModal: PropTypes.bool,
}

export default withStyle(LifeCycleEditor)
